import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, User, Info, AlertTriangle, RefreshCw } from "lucide-react";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { getEnrollmentsByInstructorId } from "@/utils/firestoreService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { subscribeToMessages } from "@/utils/firebase";

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  recipientId: string;
  content: string;
  timestamp: string;
  read: boolean;
}

interface Student {
  id: string;
  userId: string;
  name: string;
  email: string;
  avatar: string;
  lastMessage?: string;
  timestamp?: string;
  unread?: boolean;
  courseName?: string;
}

const InstructorMessages = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentUser, getMessages, sendMessage, checkConversationPermission, subscribeToMessages } = useFirebase();
  const location = useLocation();
  
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  
  useEffect(() => {
    if (location.state?.recipientId && location.state?.recipientInfo) {
      console.log("Student data passed from navigation:", location.state.recipientInfo);
      
      const student: Student = {
        id: location.state.recipientInfo.id,
        userId: location.state.recipientInfo.id,
        name: location.state.recipientInfo.name || "Student",
        email: location.state.recipientInfo.email || "",
        avatar: location.state.recipientInfo.avatar || 
          `https://ui-avatars.com/api/?name=${encodeURIComponent(location.state.recipientInfo.name || 'Student')}&background=random`,
      };
      
      setSelectedStudent(student);
    }
  }, [location.state]);
  
  useEffect(() => {
    const loadStudents = async () => {
      if (!user?.id) {
        console.log("No user ID found");
        setLoading(false);
        return;
      }
      
      try {
        console.log("Loading students for instructor:", user.id);
        setLoading(true);
        setPermissionError(null);
        
        const enrollments = await getEnrollmentsByInstructorId(user.id);
        console.log("Fetched enrollments:", enrollments);
        
        if (enrollments.length === 0) {
          console.log("No students assigned to this instructor");
          setStudents([]);
          setLoading(false);
          return;
        }
        
        const assignedStudents: Student[] = enrollments.map(enrollment => ({
          id: enrollment.id || "",
          userId: enrollment.userId || "",
          name: enrollment.studentName || "Student",
          email: enrollment.studentEmail || "",
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(enrollment.studentName || 'Student')}&background=random`,
          lastMessage: "Start a conversation...",
          timestamp: new Date().toISOString(),
          unread: false,
          courseName: enrollment.courseName
        }));
        
        console.log("Assigned students:", assignedStudents);
        setStudents(assignedStudents);
        
        if (location.state?.recipientId) {
          const studentToSelect = assignedStudents.find(
            s => s.userId === location.state.recipientId
          );
          
          if (studentToSelect) {
            console.log("Selecting student from navigation:", studentToSelect);
            setSelectedStudent(studentToSelect);
            loadMessages(studentToSelect.userId);
          } else {
            if (location.state?.recipientInfo) {
              const student = {
                id: location.state.recipientId,
                userId: location.state.recipientId,
                name: location.state.recipientInfo.name || "Student",
                email: location.state.recipientInfo.email || "",
                avatar: location.state.recipientInfo.avatar ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(location.state.recipientInfo.name || 'Student')}&background=random`,
              };
              
              const permissionResult = await checkConversationPermission(user.id, student.userId);
              
              if (permissionResult.permitted) {
                console.log("Adding external student to list:", student);
                setStudents(prev => [...prev, student]);
                setSelectedStudent(student);
                loadMessages(student.userId);
              } else {
                console.warn("No permission to message external student:", permissionResult.reason);
                toast({
                  title: "Permission Denied",
                  description: "You don't have permission to message this student. They must be assigned to one of your course enrollments by an admin.",
                  variant: "destructive",
                });
              }
            }
          }
        } else if (assignedStudents.length > 0) {
          setSelectedStudent(assignedStudents[0]);
          loadMessages(assignedStudents[0].userId);
        }
      } catch (error) {
        console.error("Error loading students:", error);
        toast({
          title: "Error",
          description: "Failed to load assigned students.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadStudents();
  }, [user, toast, location.state, checkConversationPermission]);
  
  useEffect(() => {
    if (!currentUser?.uid || !selectedStudent?.userId) return;
    
    console.log("Setting up message subscription");
    const unsubscribe = subscribeToMessages(
      currentUser.uid, 
      selectedStudent.userId,
      (newMessages) => {
        console.log("Received new messages:", newMessages);
        setMessages(newMessages);
        
        if (newMessages.length > 0) {
          const lastMsg = newMessages[newMessages.length - 1];
          updateStudentLastMessage(selectedStudent.userId, lastMsg.content, lastMsg.timestamp);
        }
      }
    );
    
    return () => {
      console.log("Cleaning up message subscription");
      unsubscribe();
    };
  }, [currentUser?.uid, selectedStudent?.userId]);
  
  const loadMessages = async (studentId: string) => {
    if (!currentUser?.uid) return;
    
    try {
      setLoadingMessages(true);
      setPermissionError(null);
      
      const permissionResult = await checkConversationPermission(currentUser.uid, studentId);
      
      if (!permissionResult.permitted) {
        console.log("No permission to message student:", permissionResult.reason);
        let errorMessage = permissionResult.reason || "You don't have permission to message this student.";
        
        if (errorMessage.includes("No instructor-student relationship")) {
          errorMessage = "You don't have permission to message this student. They must be assigned to one of your course enrollments by an admin.";
        } else if (errorMessage.includes("not found in database")) {
          errorMessage = "There was a problem with user information. Please contact an administrator.";
        }
        
        setPermissionError(errorMessage);
        setMessages([]);
        setLoadingMessages(false);
        return;
      }
      
      console.log("Loading messages between", currentUser.uid, "and", studentId);
      const fetchedMessages = await getMessages(currentUser.uid, studentId);
      console.log("Fetched messages:", fetchedMessages);
      setMessages(fetchedMessages);
      
      if (fetchedMessages.length > 0) {
        const lastMsg = fetchedMessages[fetchedMessages.length - 1];
        updateStudentLastMessage(studentId, lastMsg.content, lastMsg.timestamp);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
      toast({
        title: "Error",
        description: "Failed to load messages.",
        variant: "destructive",
      });
    } finally {
      setLoadingMessages(false);
    }
  };
  
  const updateStudentLastMessage = (studentId: string, message: string, timestamp: string) => {
    setStudents(prev => 
      prev.map(student => 
        student.userId === studentId 
          ? { ...student, lastMessage: message, timestamp } 
          : student
      )
    );
  };
  
  const handleSelectStudent = (student: Student) => {
    setSelectedStudent(student);
    loadMessages(student.userId);
  };
  
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentUser?.uid || !selectedStudent) return;
    
    try {
      const permissionResult = await checkConversationPermission(currentUser.uid, selectedStudent.userId);
      
      if (!permissionResult.permitted) {
        setPermissionError(permissionResult.reason || "You don't have permission to message this student.");
        toast({
          title: "Permission Denied",
          description: "You can only message students assigned to your courses.",
          variant: "destructive",
        });
        return;
      }
      
      const messageData = {
        senderId: currentUser.uid,
        senderName: currentUser.displayName || "Instructor",
        recipientId: selectedStudent.userId,
        content: newMessage,
        timestamp: new Date().toISOString(),
        read: false
      };
      
      console.log("Sending message:", messageData);
      const messageId = await sendMessage(messageData);
      
      const newMessageObj: Message = {
        id: messageId,
        ...messageData
      };
      
      setMessages(prev => [...prev, newMessageObj]);
      
      updateStudentLastMessage(selectedStudent.userId, newMessage, new Date().toISOString());
      
      setNewMessage("");
    } catch (error: any) {
      console.error("Error sending message:", error);
      let errorMessage = "Failed to send message.";
      if (error.message && error.message.includes("Cannot send message:")) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };
  
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return "Invalid time";
    }
  };
  
  const formatMessageDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString();
    } catch (error) {
      return "Invalid date";
    }
  };
  
  const renderDebugInfo = () => {
    if (!permissionError || !currentUser?.uid || !selectedStudent) return null;
    
    return (
      <Alert className="mt-4">
        <Info className="h-4 w-4" />
        <AlertTitle>Debug Information</AlertTitle>
        <AlertDescription className="text-xs">
          Instructor ID: {currentUser.uid}<br />
          Student ID: {selectedStudent.userId}<br />
          Error: {permissionError}
        </AlertDescription>
      </Alert>
    );
  };
  
  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Messages</h1>
            <p className="text-gray-500 mt-2">Communicate with your students</p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => {
              if (user?.id) {
                toast({
                  title: "Refreshing",
                  description: "Refreshing student list...",
                });
                setLoading(true);
                getEnrollmentsByInstructorId(user.id).then(enrollments => {
                  if (selectedStudent) {
                    loadMessages(selectedStudent.userId);
                  }
                });
              }
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Student List
          </Button>
        </div>
        
        <Card className="h-[calc(100vh-12rem)]">
          <div className="grid h-full md:grid-cols-[300px_1fr]">
            <div className="border-r">
              <CardHeader>
                <CardTitle>Assigned Students</CardTitle>
              </CardHeader>
              <ScrollArea className="h-[calc(100vh-16rem)]">
                <div className="space-y-2 p-4">
                  {loading ? (
                    <div className="flex justify-center py-4">
                      <p className="text-gray-500">Loading students...</p>
                    </div>
                  ) : students.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <User className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-gray-600 font-medium">No students assigned</p>
                      <p className="text-xs text-gray-500 text-center mt-1">
                        Students will appear here once they've been assigned to you by an admin.
                      </p>
                    </div>
                  ) : (
                    students.map((student) => (
                      <div
                        key={student.userId}
                        className={`flex items-center gap-4 p-3 rounded-lg hover:bg-accent cursor-pointer ${
                          selectedStudent?.userId === student.userId
                            ? "bg-accent/50"
                            : ""
                        }`}
                        onClick={() => handleSelectStudent(student)}
                      >
                        <Avatar className="h-10 w-10">
                          <img src={student.avatar} alt={student.name} />
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold truncate">{student.name}</h3>
                            <span className="text-xs text-gray-500">
                              {student.timestamp ? formatTimestamp(student.timestamp) : ""}
                            </span>
                          </div>
                          {student.courseName && (
                            <p className="text-xs text-gray-500">{student.courseName}</p>
                          )}
                          <p className="text-sm text-gray-500 truncate">
                            {student.lastMessage || "Start a conversation..."}
                          </p>
                        </div>
                        {student.unread && (
                          <div className="w-2 h-2 bg-primary rounded-full" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
            
            <div className="flex flex-col">
              <CardHeader className="border-b">
                {selectedStudent ? (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <img src={selectedStudent.avatar} alt={selectedStudent.name} />
                    </Avatar>
                    <div>
                      <CardTitle>{selectedStudent.name}</CardTitle>
                      <p className="text-xs text-gray-500">{selectedStudent.email}</p>
                      {selectedStudent.courseName && (
                        <p className="text-xs text-gray-400">{selectedStudent.courseName}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <CardTitle>Select a student</CardTitle>
                )}
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[calc(100vh-24rem)] p-4">
                  {permissionError ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
                      <p className="text-gray-700 font-medium">{permissionError}</p>
                      {renderDebugInfo()}
                    </div>
                  ) : selectedStudent ? (
                    loadingMessages ? (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-gray-500">Loading messages...</p>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center py-8">
                        <MessageSquare className="h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-gray-600 font-medium">No messages yet</p>
                        <p className="text-xs text-gray-500 max-w-xs mt-1">
                          Send your first message to {selectedStudent.name} to start a conversation.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((message, index) => {
                          const isCurrentUser = message.senderId === currentUser?.uid;
                          const showDate =
                            index === 0 ||
                            formatMessageDate(message.timestamp) !==
                              formatMessageDate(messages[index - 1].timestamp);
                            
                          return (
                            <div key={message.id} className="space-y-2">
                              {showDate && (
                                <div className="flex justify-center my-4">
                                  <div className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
                                    {formatMessageDate(message.timestamp)}
                                  </div>
                                </div>
                              )}
                              <div
                                className={`flex ${
                                  isCurrentUser ? "justify-end" : "justify-start"
                                }`}
                              >
                                <div
                                  className={`max-w-[80%] rounded-lg p-3 ${
                                    isCurrentUser
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-muted"
                                  }`}
                                >
                                  <p className="text-sm">{message.content}</p>
                                  <p
                                    className={`text-xs mt-1 ${
                                      isCurrentUser
                                        ? "text-primary-foreground/80"
                                        : "text-muted-foreground"
                                    }`}
                                  >
                                    {formatTimestamp(message.timestamp)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messageEndRef} />
                      </div>
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <MessageSquare className="h-10 w-10 text-gray-400 mb-3" />
                      <p className="text-gray-600 font-medium">Select a student to start messaging</p>
                      <p className="text-sm text-gray-500 max-w-xs mt-1">
                        Choose a student from the list to send them a message.
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
              {selectedStudent && !permissionError && (
                <div className="p-4 border-t mt-auto">
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Type a message..."
                      className="flex-1 min-h-[60px] max-h-[120px]"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                    />
                    <Button
                      className="self-end"
                      size="icon"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default InstructorMessages;
